import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { NodeKey } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TableOfContentsPlugin as LexicalTableOfContentsPlugin } from '@lexical/react/LexicalTableOfContentsPlugin';
import { TableOfContentsEntry } from '@lexical/react/LexicalTableOfContentsPlugin';
import './index.css';
import styled from 'styled-components';
import { Config } from '../../../RichTextEditor/types';

const MARGIN_ABOVE_EDITOR = 100;
const HEADING_WIDTH = 9;

const TableOfContentsWrapper = styled.div<{
  fontSize?: number;
  fontFamily?: string;
  textColor?: string;
  dividerHeight?: number;
}>`
  & > .headings {
    height: auto;
    max-height: ${props =>
      typeof props.dividerHeight === 'number' ? props.dividerHeight : 300}px;
    border-left: 5px solid ${props => props.textColor || '#67696d'};
    margin-left: 1rem;
    max-width: 16em;
  }

  & > .headings > .first-heading {
    color: ${props => props.textColor || '#67696d'};
    font-size: ${props => props.fontSize || 16}px;
    font-family: ${props => props.fontFamily || 'OptumSans, sans-serif'};
  }

  & > .headings > .normal-heading-wrapper {
    color: ${props => props.textColor || '#67696d'};
  }
`;

const FirstHeading = styled.label<{
  lineSpacing?: number;
  characterSpacing?: number;
}>`
  line-height: ${props => props.lineSpacing || 1.5};
  letter-spacing: ${props => props.characterSpacing || 0}px;
`;

const NormalHeading = styled.li<{
  lineSpacing?: number;
  characterSpacing?: number;
}>`
  line-height: ${props => props.lineSpacing || 1.5};
  letter-spacing: ${props => props.characterSpacing || 0}px;
`;

const HeadingsWrapper = styled.ul`
  overflow: auto;
  display: inline-flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const NormalHeadingWrapper = styled.div<{
  isDifferentTextColorOnActive?: boolean;
  textColor?: string;
  activeTextColor?: string;
  isHeaderActive?: boolean;
  fontSize?: number;
  fontFamily?: string;
  headerIndentation?: string;
}>`
  padding-left: ${props => props.headerIndentation}px;
  position: relative;
  padding-bottom: 4px;
  padding-top: 4px;
  font-size: ${props => props.fontSize || 16}px;
  font-family: ${props => props.fontFamily || 'OptumSans, sans-serif'};
  color: ${props =>
    props.isHeaderActive
      ? props.isDifferentTextColorOnActive
        ? props.activeTextColor
        : '#3578e5'
      : props.textColor || '#65676b'};
  & > div > li {
  }
  max-width: 18em;
  white-space: normal;
  word-wrap: break-word;
`;

function isHeadingAboveViewport(element: HTMLElement): boolean {
  const elementYPosition = element?.getClientRects()[0].y;
  return elementYPosition < MARGIN_ABOVE_EDITOR;
}

function isHeadingBelowTheTopOfThePage(element: HTMLElement): boolean {
  const elementYPosition = element?.getClientRects()[0].y;
  return elementYPosition >= MARGIN_ABOVE_EDITOR + HEADING_WIDTH;
}

export function TableOfContentsList({
  tableOfContents,
  widgetConfig,
}: {
  tableOfContents: Array<TableOfContentsEntry>;
  widgetConfig: Config;
}): JSX.Element {
  const [selectedKey, setSelectedKey] = useState('');
  const selectedIndex = useRef(0);
  const [editor] = useLexicalComposerContext();
  const {
    fontSize,
    fontFamily,
    textColor,
    activeTextColor,
    height,
    lineSpacing,
    characterSpacing,
  } = widgetConfig;

  function scrollToNode(key: NodeKey, currIndex: number) {
    editor.getEditorState().read(() => {
      const domElement = editor.getElementByKey(key);
      if (domElement !== null) {
        domElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        setSelectedKey(key);
        selectedIndex.current = currIndex;
      }
    });
  }

  function scrollCallback() {
    if (tableOfContents.length === 0) {
      selectedIndex.current = 0;
      return;
    }

    /**
     * Updates the selected index based on a condition function and an increment value.
     *
     * This function navigates through the `tableOfContents` array, starting from the current
     * `selectedIndex`, and updates the `selectedIndex` to the next valid heading that satisfies
     * the provided `conditionFn`. The navigation is done in the direction specified by the
     * `increment` value.
     *
     * @param conditionFn - A function that takes an `HTMLElement` and returns a boolean.
     *                      This function is used to determine if a heading element meets
     *                      the required condition.
     * @param increment - A number that specifies the direction and step size for navigating
     *                    through the `tableOfContents`. A positive value moves forward,
     *                    while a negative value moves backward.
     */
    const updateSelectedIndex = (
      conditionFn: (element: HTMLElement) => boolean,
      increment: number
    ) => {
      let currentHeading = editor.getElementByKey(
        tableOfContents[selectedIndex.current] &&
          tableOfContents[selectedIndex.current][0]
      );
      while (currentHeading !== null && conditionFn(currentHeading)) {
        const nextIndex = selectedIndex.current + increment;
        const nextHeading = editor.getElementByKey(
          tableOfContents[nextIndex] && tableOfContents[nextIndex][0]
        );
        if (nextHeading && conditionFn(nextHeading)) {
          selectedIndex.current = nextIndex;
        }
        currentHeading = nextHeading;
      }
      setSelectedKey(tableOfContents[selectedIndex.current][0]);
    };

    const currentHeading = editor.getElementByKey(
      tableOfContents[selectedIndex.current][0]
    );
    if (currentHeading !== null) {
      if (isHeadingBelowTheTopOfThePage(currentHeading)) {
        updateSelectedIndex(isHeadingBelowTheTopOfThePage, -1);
      } else if (isHeadingAboveViewport(currentHeading)) {
        updateSelectedIndex(isHeadingAboveViewport, 1);
      }
    }
  }

  useEffect(() => {
    const debouncedScrollCallback = debounce(scrollCallback, 10);
    function onScroll(): void {
      debouncedScrollCallback();
    }
    return () => document.removeEventListener('scroll', onScroll);
  }, [tableOfContents, editor]);

  const calculateHeaderIndentation = (headerType: string): number => {
    const headerLevel = parseInt(headerType.replace('h', ''), 10);
    return 32 + (headerLevel - 1) * 12;
  };

  return (
    <TableOfContentsWrapper
      id="rte-table-of-contents"
      className="rte-table-of-contents"
      data-testid="rte-table-of-contents"
      fontSize={fontSize}
      fontFamily={fontFamily}
      textColor={textColor}
      dividerHeight={Number(height)}
    >
      <HeadingsWrapper className="headings">
        <FirstHeading
          id="first-heading"
          className="first-heading"
          lineSpacing={lineSpacing}
          characterSpacing={characterSpacing}
        >
          Page Contents
        </FirstHeading>
        {tableOfContents.map(([key, text], index) => {
          return (
            <NormalHeadingWrapper
              isDifferentTextColorOnActive={
                widgetConfig.isDifferentTextColorOnActive
              }
              fontSize={fontSize}
              fontFamily={fontFamily}
              textColor={textColor}
              activeTextColor={activeTextColor}
              isHeaderActive={selectedKey === key}
              key={key}
              headerIndentation={calculateHeaderIndentation(
                tableOfContents[index][2]
              ).toString()}
            >
              <div>
                <NormalHeading
                  id={`normal-heading-${index}`}
                  onClick={() => scrollToNode(key, index)}
                  role="button"
                  tabIndex={0}
                  className={`normal-heading ${
                    selectedKey === key ? 'selected-heading' : ''
                  }
                    `}
                  lineSpacing={lineSpacing}
                  characterSpacing={characterSpacing}
                >
                  {text}
                </NormalHeading>
              </div>
            </NormalHeadingWrapper>
          );
        })}
      </HeadingsWrapper>
    </TableOfContentsWrapper>
  );
}

export default function TableOfContentsPlugin({ widgetConfig }) {
  return (
    <LexicalTableOfContentsPlugin>
      {tableOfContents => {
        return (
          <TableOfContentsList
            tableOfContents={tableOfContents}
            widgetConfig={widgetConfig}
          />
        );
      }}
    </LexicalTableOfContentsPlugin>
  );
}
